/**
 * Resolves the FinSuite parent origin that is allowed to receive cross-frame messages from
 * Verity, so `postMessage` can target that exact origin instead of the wildcard `'*'`.
 *
 * Broadcasting with `'*'` sends every reported path (which can include report IDs / query
 * params) to whatever page frames Verity. Restricting to the small, known set of FinSuite
 * origins closes that leak.
 */

import { isProduction } from './environmentUtils';

// FinSuite website origins allowed to embed Verity. Suffix match covers the alpha/beta/prod and
// personal-stack subdomains under the FinSuite domain families.
const FINSUITE_ORIGIN_SUFFIXES = ['.finsuite.device.finance.amazon.dev', '.fintech.device.finance.amazon.dev'] as const;
const FINSUITE_ORIGIN_EXACT = new Set<string>([
  'https://finsuite.device.finance.amazon.dev',
  'https://fintech.device.finance.amazon.dev',
]);

// FinSuite's local dev origin. Trusted ONLY in non-prod Verity bundles. This allowlist gates
// both which parent may drive Verity (navigation/theme) and which origin the current path is
// posted to, so honoring localhost in production would let any page served from that local
// origin pose as a trusted FinSuite parent — weakening the fail-closed guarantee. Excluded in
// prod via the isProduction() check in isFinSuiteOrigin.
const FINSUITE_DEV_ORIGIN = 'http://localhost:4111';

/**
 * True when `origin` is a recognized FinSuite website origin. Exact match for the bare prod
 * hosts; the dev localhost origin only outside production; otherwise an https host under a
 * FinSuite domain suffix.
 */
export const isFinSuiteOrigin = (origin: string): boolean => {
  if (FINSUITE_ORIGIN_EXACT.has(origin)) return true;
  if (origin === FINSUITE_DEV_ORIGIN) return !isProduction();
  try {
    const { protocol, hostname } = new URL(origin);
    return protocol === 'https:' && FINSUITE_ORIGIN_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
  } catch {
    return false;
  }
};

/**
 * Resolves the FinSuite parent origin to use as a `postMessage` targetOrigin, or `null` when
 * the embedding parent is not a recognized FinSuite origin (in which case the caller should
 * NOT send — fail closed).
 *
 * Uses `window.location.ancestorOrigins` (the embedding chain). `document.referrer` is not an
 * option because FinSuite mounts the iframe with `referrerPolicy="no-referrer"`, so the
 * referrer is stripped. `ancestorOrigins` is unsupported in Firefox, where this returns `null`
 * and path reporting is skipped — a deliberate fail-closed trade-off that keeps the path from
 * ever leaking to an unverified parent.
 */
export const resolveFinSuiteParentOrigin = (): string | null => {
  const ancestors = window.location.ancestorOrigins;
  const parentOrigin = ancestors && ancestors.length > 0 ? ancestors[0] : null;
  if (!parentOrigin) return null;
  return isFinSuiteOrigin(parentOrigin) ? parentOrigin : null;
};
